import { useState, useEffect } from 'react';
import { Check, Palette } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';

interface Color {
  id: string;
  hex: string;
  r?: number;
  g?: number;
  b?: number;
  ok_l?: number;
  ok_a?: number;
  ok_b?: number;
}

interface ColorFilterDropdownProps {
  selectedColors: string[];
  onColorsChange: (colors: string[]) => void;
  includeSimilar: boolean;
  onIncludeSimilarChange: (includeSimilar: boolean) => void;
  organizationId: string;
}

export function ColorFilterDropdown({
  selectedColors,
  onColorsChange,
  includeSimilar,
  onIncludeSimilarChange,
  organizationId,
}: ColorFilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const [tempSelectedColors, setTempSelectedColors] = useState<string[]>(selectedColors);
  const [tempIncludeSimilar, setTempIncludeSimilar] = useState(includeSimilar);
  const [availableColors, setAvailableColors] = useState<Color[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load available colors when dropdown opens
  useEffect(() => {
    if (!open || !organizationId) return;
    
    const loadColors = async () => {
      setIsLoading(true);
      try {
        // Get unique colors used in this organization's photos
        // Filter by organization_id to only show colors from user's organization
        const { data: colors, error } = await supabase
          .from('colors')
          .select('id, hex, r, g, b, ok_l, ok_a, ok_b')
          .eq('organization_id', organizationId)
          .limit(1000);

        if (error) {
          console.error('Error loading colors:', error);
          return;
        }

        if (colors) {
          // Sort colors by hue and lightness for visual grouping
          const sortedColors = colors.sort((a, b) => {
            // Calculate hue from ok_a and ok_b (similar to Lab color space)
            const hueA = Math.atan2(a.ok_b || 0, a.ok_a || 0);
            const hueB = Math.atan2(b.ok_b || 0, b.ok_a || 0);
            
            // First sort by hue, then by lightness
            if (Math.abs(hueA - hueB) > 0.1) {
              return hueA - hueB;
            }
            return (a.ok_l || 0) - (b.ok_l || 0);
          });
          
          setAvailableColors(sortedColors);
        }
      } catch (error) {
        console.error('Error loading colors:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadColors();
  }, [open, organizationId]);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      setTempSelectedColors(selectedColors);
      setTempIncludeSimilar(includeSimilar);
    }
  };

  const toggleColor = (colorId: string) => {
    if (tempSelectedColors.includes(colorId)) {
      setTempSelectedColors(tempSelectedColors.filter(id => id !== colorId));
    } else {
      setTempSelectedColors([...tempSelectedColors, colorId]);
    }
  };

  const handleApply = () => {
    onColorsChange(tempSelectedColors);
    onIncludeSimilarChange(tempIncludeSimilar);
    setOpen(false);
  };

  const tempSelectedColorObjects = availableColors.filter(color => 
    tempSelectedColors.includes(color.id)
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 relative">
          <Palette className="h-4 w-4" />
          {selectedColors.length > 0 && (
            <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px]">
              {selectedColors.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="z-50 bg-popover shadow-md w-80 p-0" align="end">
        <div className="flex flex-col">
          <div className="p-3 border-b">
            <h4 className="font-medium text-sm">Colors</h4>
            {tempSelectedColorObjects.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {tempSelectedColorObjects.map((color) => (
                  <div
                    key={color.id}
                    className="w-6 h-6 rounded border-2 border-white shadow-sm"
                    style={{ backgroundColor: color.hex }}
                    title={color.hex}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="p-3 border-b flex items-center justify-between">
            <Label htmlFor="include-similar" className="text-sm">
              Include similar colors
            </Label>
            <Switch
              id="include-similar"
              checked={tempIncludeSimilar}
              onCheckedChange={setTempIncludeSimilar}
            />
          </div>

          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search colors"
              className="h-9"
            />
            <CommandList>
              <CommandEmpty>
                {isLoading ? 'Loading colors...' : 'No colors found.'}
              </CommandEmpty>
              <CommandGroup>
                <div className="grid grid-cols-8 gap-2 p-2">
                  {availableColors.map((color) => (
                    <button
                      key={color.id}
                      onClick={() => toggleColor(color.id)}
                      className={cn(
                        "w-8 h-8 rounded border-2 relative transition-all hover:scale-110",
                        tempSelectedColors.includes(color.id)
                          ? "border-primary ring-2 ring-primary/50"
                          : "border-border"
                      )}
                      style={{ backgroundColor: color.hex }}
                      title={color.hex}
                    >
                      {tempSelectedColors.includes(color.id) && (
                        <Check className="absolute inset-0 m-auto h-4 w-4 text-white drop-shadow-md" />
                      )}
                    </button>
                  ))}
                </div>
              </CommandGroup>
            </CommandList>
          </Command>

          <div className="p-3 border-t flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => {
                setTempSelectedColors([]);
                setTempIncludeSimilar(false);
                onColorsChange([]);
                onIncludeSimilarChange(false);
                setOpen(false);
              }}
            >
              Clear
            </Button>
            <Button
              size="sm"
              className="flex-1"
              onClick={handleApply}
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
